<app-panel-body>
  <div class="flex min-h-0 flex-1 flex-col" *transloco="let t; prefix: 'trip-finances.money-in-settings'">
    <div class="min-h-0 flex-1 overflow-y-auto">
      <app-content-container>
        @if (hasEnded()) {
          <p class="text-sm text-primary-text-light" data-testid="ended-trip-notice">
            {{ t('ended-trip-notice') }}
          </p>
        }

        <section data-testid="individual-trip-payments-section">
          <h3 class="text-base font-semibold text-primary mb-4">{{ t('individual-trip-payments-section') }}</h3>

          <div class="flex items-center justify-between gap-4">
            <div class="flex items-start gap-2">
              <div class="text-sm font-medium">{{ t('collect-online-payments-label') }}</div>
              <app-info-popover [triggerAriaLabel]="t('collect-online-payments-info-label')">
                <p>{{ t('collect-online-payments-info-immediate') }}</p>
                <p>{{ t('collect-online-payments-info-receipt') }}</p>
                <p>{{ t('collect-online-payments-info-schedules') }}</p>
                <p>{{ t('collect-online-payments-info-fees') }}</p>
              </app-info-popover>
            </div>
            <ion-toggle
              data-testid="collect-online-payments-toggle"
              [checked]="isParticipantPaymentsEnabled()"
              [disabled]="participantPaymentsSubmitting() || hasEnded()"
              (ionChange)="onParticipantPaymentsChange($event)"
              [attr.aria-label]="isParticipantPaymentsEnabled() ? t('disable-collect-online-payments') : t('enable-collect-online-payments')"
            />
          </div>
        </section>

        <section data-testid="online-fundraising-section">
          <h3 class="text-base font-semibold text-primary mb-4">{{ t('online-fundraising-section') }}</h3>

          @if (!isStandardTrip()) {
            <p class="text-sm text-primary-text-light">{{ t('designation-not-eligible') }}</p>
          } @else {
            @if (hasEnded() && !hasAgreement()) {
              <div class="flex items-center justify-between gap-4 mb-4">
                <div class="flex items-start gap-2">
                  <div class="text-sm font-medium">{{ t('fundraising-label') }}</div>
                  <app-info-popover [triggerAriaLabel]="t('fundraising-info-label')">
                    <p>{{ t('fundraising-info-description') }}</p>
                    <p>{{ t('interest-page-exclusivity') }}</p>
                  </app-info-popover>
                </div>
                <ion-toggle
                  data-testid="fundraising-toggle"
                  [checked]="false"
                  [disabled]="true"
                  [attr.aria-label]="t('enable-fundraising')"
                />
              </div>
              <p class="text-sm text-primary-text-light" data-testid="ended-never-enabled-fundraising">
                {{ t('ended-never-enabled-fundraising') }}
              </p>
            } @else {
              <div class="flex flex-col gap-4">
              <div class="flex items-center justify-between gap-4">
                <div class="flex flex-col gap-1">
                  <div class="flex items-start gap-2">
                    <div class="text-sm font-medium">{{ t('fundraising-label') }}</div>
                    <app-info-popover [triggerAriaLabel]="t('fundraising-info-label')">
                      <p>{{ t('fundraising-info-description') }}</p>
                      <p>{{ t('interest-page-exclusivity') }}</p>
                    </app-info-popover>
                  </div>
                  @if (isInterestPageActive() && !isEnabled()) {
                    <p class="text-sm text-warning" data-testid="interest-page-active-warning">
                      {{ t('interest-page-active-warning') }}
                    </p>
                  }
                </div>
                @if (fundraisingToggleMounted()) {
                  <ion-toggle
                    data-testid="fundraising-toggle"
                    [checked]="activateChecked()"
                    [disabled]="fundraisingToggleSubmitting() || hasEnded()"
                    (ionChange)="onActivateFundraisingChange($event)"
                    [attr.aria-label]="activateChecked() ? t('disable-fundraising') : t('enable-fundraising')"
                  />
                }
              </div>

              @if (agreementUrl() && !isEnabled()) {
                <a
                  [href]="agreementUrl()"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-link underline text-sm"
                  data-testid="agreement-download-link"
                >
                  {{ t('agreement-download-button') }}
                </a>
              }

              @if (!hasAgreement() && activateIntent()) {
                <div>
                  @if (templateLoading()) {
                    <p class="text-sm text-primary-text-light">{{ t('agreement-loading') }}</p>
                  } @else if (templateError()) {
                    <p class="text-sm text-danger">{{ t('agreement-load-error') }}</p>
                  } @else if (agreementTemplate(); as template) {
                    <div class="mb-2">
                      <div class="text-sm font-medium mb-1">{{ t('agreement-title') }}</div>
                    </div>
                    <div class="flex flex-col gap-4 mb-4">
                      @for (section of template.sections; track section.key) {
                        <app-legal-signature-section [section]="section" />
                      }
                    </div>
                  }

                  <div class="mb-4">
                    <div class="flex items-start gap-2 mb-1">
                      <label class="text-sm font-medium" for="fs-mm-goal">{{ t('mm-goal-label') }}</label>
                      <app-info-popover [triggerAriaLabel]="t('mm-goal-info-label')">
                        <p>{{ t('mm-goal-info-team-total') }}</p>
                        <p>{{ t('mm-goal-info-per-head') }}</p>
                      </app-info-popover>
                    </div>
                    <p class="text-xs text-primary-text-light mb-2">{{ t('mm-goal-description') }}</p>
                    <input
                      id="fs-mm-goal"
                      type="number"
                      class="w-full rounded-md border border-primary-border bg-primary-bg px-3 py-2 text-sm"
                      [ngModel]="setupMmGoal()"
                      (ngModelChange)="setupMmGoal.set($event)"
                      min="0"
                      [attr.aria-label]="t('mm-goal-label')"
                    />
                  </div>

                  <app-faq
                    class="mb-4 block"
                    data-testid="ministry-money-faq"
                    [itemIds]="onboardingFaqItemIds"
                    [translationPrefix]="onboardingFaqPrefix"
                    [showIntro]="false"
                  />

                  @if (agreementTemplate()) {
                    @if (!setupSubmitReadiness().canSubmit) {
                      <p id="fundraising-setup-submit-hint" class="text-sm text-secondary-text mb-2">
                        {{ t('submit-incomplete-hint') }}
                      </p>
                    }
                    <button
                      type="button"
                      class="btn btn-primary w-full"
                      data-testid="fundraising-setup-submit"
                      [disabled]="setupSubmitDisabled()"
                      [attr.aria-disabled]="setupSubmitDisabled() ? 'true' : null"
                      [attr.aria-describedby]="!setupSubmitReadiness().canSubmit ? 'fundraising-setup-submit-hint' : null"
                      (click)="submitSetup()"
                    >
                      @if (setupSubmitting()) {
                        <fa-icon [icon]="['far', 'spinner']" [animation]="'spin'" class="mr-2" />
                      }
                      {{ t('enable-button') }}
                    </button>
                  }
                </div>
              }

              @if (isEnabled()) {
                <p class="text-sm text-primary-text-light" data-testid="interest-page-exclusivity">
                  {{ t('interest-page-exclusivity') }}
                </p>
                <div>
                  <div class="text-sm font-medium mb-1">{{ t('trip-url-label') }}</div>
                  @if (tripId(); as id) {
                    <app-trip-link-qr [tripId]="id" />
                  }
                </div>

                <div class="flex items-center justify-between gap-4">
                  <div class="flex items-start gap-2">
                    <div class="text-sm font-medium">{{ t('public-label') }}</div>
                    <app-info-popover [triggerAriaLabel]="t('public-info-label')">
                      <p>{{ t('public-info-gifts') }}</p>
                      <p>{{ t('public-info-cutoff') }}</p>
                    </app-info-popover>
                  </div>
                  <ion-toggle
                    data-testid="public-fundraising-toggle"
                    [checked]="isPublic()"
                    [disabled]="toggleSubmitting() || hasEnded()"
                    (ionChange)="onPublicFundraisingChange($event)"
                    [attr.aria-label]="isPublic() ? t('disable-public') : t('enable-public')"
                  />
                </div>

                <div>
                  <div class="flex items-start gap-2 mb-1">
                    <label class="text-sm font-medium" for="fs-mm-goal-edit">{{ t('mm-goal-label') }}</label>
                    <app-info-popover [triggerAriaLabel]="t('mm-goal-info-label')">
                      <p>{{ t('mm-goal-info-team-total') }}</p>
                      <p>{{ t('mm-goal-info-per-head') }}</p>
                    </app-info-popover>
                  </div>
                  <p class="text-xs text-primary-text-light mb-2">{{ t('mm-goal-description') }}</p>
                  <div class="flex gap-2">
                    <input
                      id="fs-mm-goal-edit"
                      type="number"
                      class="flex-1 rounded-md border border-primary-border bg-primary-bg px-3 py-2 text-sm"
                      [ngModel]="mmGoalEdit()"
                      (ngModelChange)="mmGoalEdit.set($event)"
                      min="0"
                      [disabled]="hasEnded()"
                      [attr.aria-label]="t('mm-goal-label')"
                    />
                    @if (!hasEnded()) {
                      <button
                        type="button"
                        class="btn btn-primary btn-no-grow"
                        [disabled]="mmGoalSubmitting()"
                        (click)="saveMmGoal()"
                      >
                        @if (mmGoalSubmitting()) {
                          <fa-icon [icon]="['far', 'spinner']" [animation]="'spin'" />
                        } @else {
                          {{ t('save') }}
                        }
                      </button>
                    }
                  </div>
                </div>

                @if (agreementUrl()) {
                  <a
                    [href]="agreementUrl()"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-link underline text-sm"
                    data-testid="agreement-download-link"
                  >
                    {{ t('agreement-download-button') }}
                  </a>
                }
              }
              </div>
            }
          }
        </section>
      </app-content-container>
    </div>
  </div>
</app-panel-body>
